import type { LinkProps as RACLinkProps } from 'react-aria-components/Link';
import { Link as RACLink } from 'react-aria-components/Link';

import type { ButtonVariantProps } from '../button/button';
import { buttonVariants } from '../button/button';

export type LinkButtonProps = RACLinkProps &
  ButtonVariantProps & {
    isActive?: boolean;
  };

/**
 * A link that looks like a button. Use this component for navigation.
 *
 * See `Link` for a regular link, and `Button` for a button that triggers actions.
 *
 * See https://react-spectrum.adobe.com/react-aria/Link.html for more details.
 *
 * ```tsx
 * <LinkButton icon={<Icon name="chevron_small_isolated_w" />} variant="primary" size="m" href="/home">
 *   Go to home
 * </LinkButton>
 * ```
 */
export const LinkButton = ({
  variant,
  size,
  round,
  className,
  icon,
  endIcon,
  children,
  isActive,
  ...props
}: LinkButtonProps) => {
  const hasChildren = Boolean(children);
  const classes = buttonVariants({
    size,
    variant,
    round,
    className,
    withIcon: hasChildren && Boolean(icon),
    withEndIcon: hasChildren && Boolean(endIcon),
    iconOnly: hasChildren === false && Boolean(icon),
    isActive: Boolean(isActive),
  });

  return (
    <RACLink {...props} className={classes}>
      {icon}
      {children}
      {endIcon}
    </RACLink>
  );
};
